import { FormNotice /* , TextInput */ } from '@zappyrent/hundop';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { upperCase, upperFirst } from 'lodash';
import { rem } from 'polished';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import shallow from 'zustand/shallow';

import { IUser } from '../../../shared/ts/user';
import useStore from '../../../store';
import TelephoneInput from '../../___design/TelephoneInput';
import TextInput from '../../___design/TextInput';
import {
  StyledCtaButton,
  StyledEditButton,
  StyledFieldList,
  StyledFieldListItem,
  StyledFieldset,
} from './UserInfoFields.styles';

interface IUserInfoFieldsState {
  errorLabel: string;
  hasErrors: boolean;
  initValue: string | null | undefined;
  isEditing: boolean;
  isSubmitClicked: boolean;
  label: string;
  name: string;
  type: 'text' | 'date' | 'tel';
  value: string | null | undefined;
}

const UserInfoFields = (): JSX.Element | null => {
  const { t } = useTranslation('form');
  const { user, updateUserById } = useStore(
    state => ({
      user: state.user,
      updateUserById: state.updateUserById,
    }),
    shallow,
  );
  const defaultFormErrorLabel = t('requiredField');
  const [fields, setFields] = useState<IUserInfoFieldsState[]>([
    {
      errorLabel: defaultFormErrorLabel,
      hasErrors: false,
      initValue: user?.first_name ?? '',
      isEditing: false,
      isSubmitClicked: false,
      label: upperFirst(t('firstName')),
      name: 'first_name',
      type: 'text',
      value: user?.first_name ?? '',
    },
    {
      errorLabel: defaultFormErrorLabel,
      hasErrors: false,
      initValue: user?.last_name ?? '',
      isEditing: false,
      isSubmitClicked: false,
      label: upperFirst(t('lastName')),
      name: 'last_name',
      type: 'text',
      value: user?.last_name ?? '',
    },
    {
      errorLabel: defaultFormErrorLabel,
      hasErrors: false,
      initValue: user?.birthday ?? '',
      isEditing: false,
      isSubmitClicked: false,
      label: t('form:dateOfBirth'),
      name: 'birthday',
      type: 'date',
      value: user?.birthday ?? '',
    },
    {
      errorLabel: defaultFormErrorLabel,
      hasErrors: false,
      initValue: user?.city ?? '',
      isEditing: false,
      isSubmitClicked: false,
      label: t('form:city'),
      name: 'city',
      type: 'text',
      value: user?.city ?? '',
    },
    {
      errorLabel: t('form:phoneIsNotValid'),
      hasErrors: false,
      initValue: user?.phone_no ?? '',
      isEditing: false,
      isSubmitClicked: false,
      label: t('form:phoneNumber'),
      name: 'phone_no',
      type: 'tel',
      value: user?.phone_no ?? '',
    },
  ]);

  const isStandby = () => {
    return [...fields].every(item => item.isEditing === false);
  };

  const resetIsEditing = () => {
    const arr = [...fields].map(item => {
      item.isEditing = false;
      item.value = item.initValue;
      return item;
    });
    setFields(arr);
  };

  const closeIsEditing = (name: string) => {
    const arr = [...fields].map(item =>
      item.name === name
        ? {
            ...item,
            isEditing: false,
            value: item.initValue,
          }
        : { ...item },
    );
    setFields(arr);
  };

  const openIsEditing = (name: string) => {
    const arr = [...fields].map(item =>
      item.name !== name
        ? { ...item, isEditing: false }
        : { ...item, isEditing: true },
    );
    setFields(arr);
  };

  const hasFieldErrors = (name: string, value: string) => {
    if ((name === 'phone_no' && !isValidPhoneNumber(value)) || !value) {
      return true;
    }

    return false;
  };

  const saveItem = (name: string, value: string) => {
    const arr = [...fields].map(item =>
      item.name === name
        ? { ...item, initValue: value, isEditing: false }
        : { ...item },
    );
    setFields(arr);

    try {
      const { id } = user as IUser;
      const payload = {
        [name]: name === 'phone_no' ? value.split(' ').join('') : value,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;

      updateUserById(id, payload);
    } catch (err) {
      console.error(err);
    }
  };

  const handleInputchange = (
    name: string,
    value: string,
    isSubmitClicked: boolean,
  ) => {
    const hasErrors: boolean = hasFieldErrors(name, value);
    const arr = [...fields].map(item =>
      item.name === name
        ? { ...item, isSubmitClicked, hasErrors, value }
        : { ...item },
    );
    setFields(arr);
  };

  const handleEditButtonClick = (name: string, isEditing: boolean) => {
    isEditing ? closeIsEditing(name) : openIsEditing(name);
  };

  const handleSaveButtonClick = (name: string, value: string) => {
    const hasErrors: boolean = hasFieldErrors(name, value);
    const arr = [...fields].map(item =>
      item.name === name
        ? { ...item, isSubmitClicked: true, hasErrors }
        : { ...item },
    );
    setFields(arr);
    if (!hasErrors) saveItem(name, value);
  };

  useEffect(() => {
    resetIsEditing();
  }, [user?.type]);

  if (user) {
    const { type: userType } = user;

    return (
      <StyledFieldset>
        <StyledFieldList>
          {fields.map(item => {
            const {
              errorLabel,
              hasErrors,
              isEditing,
              isSubmitClicked,
              label,
              name,
              type,
              value,
            } = item;

            return (
              <StyledFieldListItem key={name}>
                <StyledEditButton
                  disabled={!isEditing && !isStandby()}
                  user={userType}
                  onClick={() => handleEditButtonClick(name, isEditing)}
                >
                  {isEditing ? t('form:cancel') : t('form:edit')}
                </StyledEditButton>

                {type === 'tel' ? (
                  <TelephoneInput
                    id={name}
                    label={label}
                    name={name}
                    value={value as string}
                    disabled={!isEditing}
                    onChange={value =>
                      handleInputchange(name, value, isSubmitClicked)
                    }
                  >
                    {isEditing && isSubmitClicked && hasErrors && (
                      <FormNotice isWrapped>{errorLabel}</FormNotice>
                    )}
                  </TelephoneInput>
                ) : (
                  <TextInput
                    disabled={!isEditing}
                    id={name}
                    label={label}
                    name={name}
                    type={type}
                    value={value as string}
                    onChange={event =>
                      handleInputchange(
                        name,
                        event.target.value,
                        isSubmitClicked,
                      )
                    }
                  >
                    {isEditing && isSubmitClicked && hasErrors && (
                      <FormNotice isWrapped>{errorLabel}</FormNotice>
                    )}
                  </TextInput>
                )}
                {isEditing && (
                  <StyledCtaButton
                    user={userType}
                    width={rem('128px')}
                    onClick={() => handleSaveButtonClick(name, value as string)}
                  >
                    {upperCase(t('form:save'))}
                  </StyledCtaButton>
                )}
              </StyledFieldListItem>
            );
          })}
          <StyledFieldListItem>
            <TextInput
              disabled={true}
              id="email"
              label="Email"
              name="email"
              type="email"
              value={user?.email}
            />
          </StyledFieldListItem>
        </StyledFieldList>
      </StyledFieldset>
    );
  }

  return null;
};

export default UserInfoFields;
